import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../services/auth';
import apiService from '../../services/api';
import EmissionsPieChart from './EmissionsPieChart';
import ActivityBarChart from './ActivityBarChart';
import EmissionsStageBarChart from './EmissionsStageBarChart';
import Button, { ButtonGroup } from '../Common/Button';
import { formatLargeNumber, formatDateWithTimezone } from '../../utils/helpers';
import {
  RefreshCw,
  FileText,
  FileDown,
  TrendingUp,
  Activity,
  Layers,
  ChevronRight,
  ChevronDown,
  Info,
  AlertCircle,
  Plus,
  Package,
  Truck,
  Factory,
  Construction,
  Wrench,
  Recycle
} from 'lucide-react';
import { getActivityDisplayName } from '../../utils/activityDisplayNames'; 

const formatEmissionValue = (value) => formatLargeNumber(value || 0, { decimals: 1 });

const Dashboard = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [epdStatus, setEPDStatus] = useState(null);
  const [loadingEPD, setLoadingEPD] = useState(false);
  const [loadingLCAReport, setLoadingLCAReport] = useState(false);
  const [expandedStages, setExpandedStages] = useState({});
  const [activeDistributionTab, setActiveDistributionTab] = useState('stage'); // 'stage' or 'activity'
  const [showScenarioInfo, setShowScenarioInfo] = useState(false);
  const infoButtonRef = useRef(null);
  const infoPopupRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleInfoMouseEnter = () => {
    clearHoverTimeout();
    setShowScenarioInfo(true);
  };

  const handleInfoMouseLeave = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = window.setTimeout(() => {
      setShowScenarioInfo(false);
    }, 120);
  };

  const handleInfoButtonClick = () => {
    clearHoverTimeout();
    setShowScenarioInfo((prev) => !prev);
  };

  const handleInfoButtonFocus = () => {
    clearHoverTimeout();
    setShowScenarioInfo(true);
  };

  const handleInfoButtonBlur = () => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setShowScenarioInfo(false);
    }, 120);
  };

  useEffect(() => () => {
    clearHoverTimeout();
  }, []);

  useEffect(() => {
    if (!showScenarioInfo) return;

    const handleClickOutside = (event) => {
      if (
        infoPopupRef.current &&
        !infoPopupRef.current.contains(event.target) &&
        infoButtonRef.current &&
        !infoButtonRef.current.contains(event.target)
      ) {
        clearHoverTimeout();
        setShowScenarioInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showScenarioInfo]);

  const { isAuthenticated } = useAuth();

  const stageIdToIndex = {
    a1: 0,
    a2: 1,
    a3: 2,
    a4a5: 3,
    b2b5: 4,
    c1c4: 5,
  };

  const [activeStageTile, setActiveStageTile] = useState(null);

  const loadScenarios = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('[Dashboard] Not authenticated, skipping scenario load');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('[Dashboard] Loading scenarios...');
      const response = await apiService.getScenarios();

      console.log('[Dashboard] ✅ Loaded scenarios:', response.data?.length || 0);
      setScenarios(response.data || []);
    } catch (err) {
      console.error('[Dashboard] Failed to load scenarios:', err);

      if (err.response?.status === 403) {
        console.log('[Dashboard] Auth error - will retry after login');
      } else {
        setError(err.message || 'Failed to load scenarios');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  useEffect(() => {
    const scenarioId = searchParams.get('scenario');
    if (scenarioId && scenarios.length > 0) {
      const scenario = scenarios.find((s) => s._id === scenarioId);
      if (scenario) {
        setSelectedScenario(scenario);
        if (scenario.emissions) {
          loadEPDStatus(scenario._id);
        }
      }
    } else if (scenarios.length > 0 && !selectedScenario) {
      setSelectedScenario(scenarios[0]);
      if (scenarios[0].emissions) {
        loadEPDStatus(scenarios[0]._id);
      }
    }
  }, [scenarios, searchParams, selectedScenario]);

  const loadEPDStatus = async (scenarioId) => {
    try {
      const response = await apiService.get(`/asphalt/scenarios/${scenarioId}/epd-status`);
      setEPDStatus(response.data);
    } catch (error) {
      console.error('Failed to load EPD status:', error);
    }
  };

  const handleGenerateEPD = async () => {
    if (!selectedScenario || loadingEPD) return;
    setLoadingEPD(true);

    try {
      await apiService.post(`/asphalt/scenarios/${selectedScenario._id}/generate-epd`);
      await loadEPDStatus(selectedScenario._id);
    } catch (error) {
      console.error('Failed to generate EPD:', error);
      alert(error.response?.data?.message || 'Failed to generate EPD');
    } finally {
      setLoadingEPD(false);
    }
  };

  const handleDownloadLCAReport = async () => {
    if (!selectedScenario || loadingLCAReport) return;

    setLoadingLCAReport(true);
    try {
      await apiService.exportExtendedLCAReport(selectedScenario._id);
    } catch (error) {
      console.error('Failed to download LCA report:', error);
      alert('Failed to download LCA report');
    } finally {
      setLoadingLCAReport(false);
    }
  };

  const handleDownloadEPD = async () => {
    if (!selectedScenario || loadingEPD) return;

    try {
      setLoadingEPD(true);

      if (!epdStatus?.has_epd) {
        await apiService.post(`/asphalt/scenarios/${selectedScenario._id}/generate-epd`);
        await loadEPDStatus(selectedScenario._id);
      }

      // FIX: Use direct API call with blob response type instead of apiService.downloadEPD()
      const response = await apiService.api.get(
        `/asphalt/scenarios/${selectedScenario._id}/export-epd`,
        { responseType: 'blob' }
      );

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `EPD_${selectedScenario.facility}_${selectedScenario.name}.docx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download EPD:', error);
      alert('Failed to download EPD');
    } finally {
      setLoadingEPD(false);
    }
  };

  const handleScenarioChange = (scenarioId) => {
    const scenario = scenarios.find((s) => s._id === scenarioId);

    if (scenario) {
      setSelectedScenario(scenario);
      setSearchParams({ scenario: scenarioId });
      if (scenario.emissions) {
        loadEPDStatus(scenario._id);
      }
    }
  };

  const toggleStageExpansion = (stageIndex) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageIndex]: !prev[stageIndex],
    }));
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading scenarios...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <AlertCircle size={48} />
        <h2>Error Loading Scenarios</h2>
        <p>{error}</p>
        <button onClick={loadScenarios} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (scenarios.length === 0) {
    return (
      <div className="dashboard-empty">
        <div className="empty-state">
          <Layers size={64} />
          <h2>No Scenarios Yet</h2>
          <p>Create your first LCA scenario to start analyzing emissions data.</p>
          <a href="/scenarios/create" className="btn btn-primary">
            <Plus size={18} />
            Create Scenario
          </a>
        </div>
      </div>
    );
  }

  const handleTileClick = (stageKey) => {
    setActiveStageTile(stageKey);
    setActiveDistributionTab('activity');
    const idx = stageIdToIndex[stageKey];
    if (typeof idx !== 'number') return;
    setExpandedStages({ [idx]: true });
    requestAnimationFrame(() => {
      document.getElementById(`breakdown-card-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="dashboard-header-main">
          <div className="dashboard-header-info">
            <div className="dashboard-title-row">
              <h1 className="dashboard-title">Emissions Dashboard</h1>
              {selectedScenario && (
                <div
                  className="scenario-info-trigger"
                  ref={infoButtonRef}
                  onMouseEnter={handleInfoMouseEnter}
                  onMouseLeave={handleInfoMouseLeave}
                >
                  <button
                    type="button"
                    className="scenario-info-button"
                    onClick={handleInfoButtonClick}
                    onFocus={handleInfoButtonFocus}
                    onBlur={handleInfoButtonBlur}
                    aria-label="Scenario details"
                    aria-expanded={showScenarioInfo}
                  >
                    <Info size={18} />
                  </button>

                  {showScenarioInfo && (
                    <ScenarioInfoPopover
                      ref={infoPopupRef}
                      scenario={selectedScenario}
                      onMouseEnter={handleInfoMouseEnter}
                      onMouseLeave={handleInfoMouseLeave}
                    />
                  )}
                </div>
              )}
            </div>
            <p className="dashboard-subtitle">Lifecycle carbon analysis and reporting</p>
          </div>

          <div className="dashboard-header-actions">
            <select
              className="scenario-selector"
              value={selectedScenario?._id || ''}
              onChange={(e) => handleScenarioChange(e.target.value)}
            >
              {scenarios.map((scenario) => (
                <option key={scenario._id} value={scenario._id}>
                  {scenario.name}
                </option>
              ))}
            </select>

            <ButtonGroup gap="md" className="dashboard-action-buttons" align="right">

              <Button
                variant="secondary"
                onClick={handleDownloadLCAReport}
                disabled={!selectedScenario?.emissions}
                loading={loadingLCAReport}
                title={
                  selectedScenario?.emissions
                    ? 'Download Extended LCA Report'
                    : 'Calculate scenario to enable LCA report download'
                }
                icon={!loadingLCAReport ? <FileDown size={18} /> : undefined}
              >
                Download LCA Report
              </Button>

              <Button
                variant="primary"
                onClick={handleDownloadEPD}
                disabled={!selectedScenario?.emissions}
                loading={loadingEPD}
                title={!selectedScenario?.emissions
                  ? 'Calculate scenario to enable EPD download'
                  : epdStatus?.has_epd
                    ? 'Download Proprietary Environmental Product Declaration'
                    : 'Download Proprietary Environmental Product Declaration'}
                icon={<FileDown size={18} />}
              >
                {epdStatus?.has_epd ? 'Download EPD' : 'Download EPD'}
              </Button>

              
            </ButtonGroup>
          </div>
        </div>
      </div>

      {!selectedScenario || !selectedScenario.emissions ? (
        <div className="empty-state">
          <div className="empty-icon">
            <AlertCircle size={64} />
          </div>
          <h2>No Emissions Data</h2>
          <p>This scenario has not been calculated yet. Please run the calculation to view results.</p>
          <a href={`/scenarios/create?edit=${selectedScenario?._id}`} className="btn btn-primary">
            Recalculate Scenario
          </a>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            {/* Total Emissions - Spans 2 rows, 1 column */}
            <div className="metric-card metric-card-primary">
              <div className="metric-card-icon">
                <TrendingUp size={28} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">Total Emissions</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_emissions)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* A1 - Raw Materials */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'a1' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('a1')}
            >
              <div className="metric-card-icon metric-card-icon-blue">
                <Package size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">A1 - Raw Materials</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_a1)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* A2 - Transport */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'a2' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('a2')}
            >
              <div className="metric-card-icon metric-card-icon-cyan">
                <Truck size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">A2 - Transport to Plant</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_a2)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* A3 - Manufacturing */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'a3' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('a3')}
            >
              <div className="metric-card-icon metric-card-icon-green">
                <Factory size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">A3 - Manufacturing</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_a3)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* A4-A5 - Construction */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'a4a5' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('a4a5')}
            >
              <div className="metric-card-icon metric-card-icon-purple">
                <Construction size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">A4-A5 - Construction</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_a4_a5 || 0)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* B2-B5 - Use & Maintenance */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'b2b5' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('b2b5')}
            >
              <div className="metric-card-icon metric-card-icon-amber">
                <Wrench size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">B2-B5 - Use Phase</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_b2_b5 || 0)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>

            {/* C1-C4 - End of Life */}
            <div
              className={`metric-card metric-card-stage stage-tile ${activeStageTile === 'c1c4' ? 'stage-tile--active' : ''}`}
              onClick={() => handleTileClick('c1c4')}
            >
              <div className="metric-card-icon metric-card-icon-red">
                <Recycle size={22} />
              </div>
              <div className="metric-card-content">
                <div className="metric-card-label">C1-C4 - End of Life</div>
                <div className="metric-card-value">
                  {formatEmissionValue(selectedScenario.emissions.total_c1_c4 || 0)}
                </div>
                <div className="metric-card-unit">kg CO₂e</div>
              </div>
            </div>
          </div>

          {/* Distribution Section with Tabs */}
          <div className="distribution-section">
            <div className="section-header">
              <div className="section-header-icon">
                <Layers size={24} />
              </div>
              <div className="section-header-text">
                <h2 className="section-title">Distribution</h2>
                <p className="section-subtitle">Emissions breakdown by stage and activity</p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="distribution-tabs">
              <button
                className={`distribution-tab ${activeDistributionTab === 'stage' ? 'active' : ''}`}
                onClick={() => setActiveDistributionTab('stage')}
              >
                <Layers size={18} />
                Stage
              </button>
              <button
                className={`distribution-tab ${activeDistributionTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveDistributionTab('activity')}
              >
                <Activity size={18} />
                Activity
              </button>
            </div>

            {/* Tab Content */}
            <div className="distribution-tab-content">
              {activeDistributionTab === 'stage' && (
                <div className="distribution-stage-view">
                  {/* Lifecycle Emissions Breakdown (Pie Chart) */}
                  {/* <div className="chart-card">
                    <div className="chart-card-header">
                      <div className="chart-card-header-text">
                        <h3 className="chart-card-title">Lifecycle Emissions Breakdown</h3>
                        <p className="chart-card-subtitle">Proportional distribution by lifecycle stage</p>
                      </div>
                    </div>
                    <div className="chart-card-body">
                      <EmissionsPieChart emissions={selectedScenario.emissions} />
                    </div>
                  </div> */}

                  {/* Lifecycle Stage Emissions Comparison (Bar Chart) */}
                  <div className="chart-card">
                    <div className="chart-card-header">
                      <div className="chart-card-header-text">
                        <h3 className="chart-card-title">Lifecycle Stage Emissions Comparison</h3>
                        <p className="chart-card-subtitle">Absolute emissions by lifecycle stage</p>
                      </div>
                    </div>
                    <div className="chart-card-body">
                      <EmissionsStageBarChart emissions={selectedScenario.emissions} />
                    </div>
                  </div>
                </div>
              )}

              {activeDistributionTab === 'activity' && (
                <div className="distribution-activity-view">
                  {/* Activity Breakdown Chart */}
                  <div className="chart-card">
                    <div className="chart-card-header">
                      <div className="chart-card-header-text">
                        <h3 className="chart-card-title">Activity Breakdown</h3>
                        <p className="chart-card-subtitle">Detailed emissions by activity type</p>
                      </div>
                    </div>
                    <div className="chart-card-body">
                      <ActivityBarChart emissions={selectedScenario.emissions} />
                    </div>
                  </div>

                  {/* Detailed Emissions Table */}
                  <DetailedBreakdown
                    emissions={selectedScenario.emissions}
                    expandedStages={expandedStages}
                    toggleStageExpansion={toggleStageExpansion}
                  />
                </div>
              )}
            </div>
          </div>

          
        </>
      )}
    </div>
  );
};


// Detailed Breakdown Component with Expandable Cards
const DetailedBreakdown = ({ emissions, expandedStages, toggleStageExpansion }) => {
  const formatActivityName = (key) => {
    return getActivityDisplayName(key);  // <-- REPLACE ENTIRE FUNCTION BODY
  };

  const stages = [
    {
      id: 'a1',
      title: 'A1 - Raw Material Extraction',
      icon: Package,
      activities: emissions.quarry_emissions,
      total: emissions.total_a1,
      color: '#1E3A8A',
      description: 'Emissions from quarry and raw materials',
    },
    {
      id: 'a2',
      title: 'A2 - Transport to Plant',
      icon: Truck,
      activities: emissions.transport_emissions,
      total: emissions.total_a2,
      color: '#0EA5E9',
      description: 'Transportation emissions from quarry to manufacturing plant',
    },
    {
      id: 'a3',
      title: 'A3 - Manufacturing',
      icon: Factory,
      activities: emissions.manufacturing_emissions,
      total: emissions.total_a3,
      color: '#10B981',
      description: 'Emissions from the manufacturing and production process',
    },
    {
      id: 'a4a5',
      title: 'A4-A5 - Construction & Installation',
      icon: Construction,
      activities: emissions.transport_from_plant_emissions,
      total: emissions.total_a4_a5 || 0,
      color: '#7C3AED',
      description: 'Transport to site and installation activities',
    },
    {
      id: 'b2b5',
      title: 'B2-B5 - Use & Maintenance',
      icon: Wrench,
      activities: emissions.maintenance_emissions,
      total: emissions.total_b2_b5 || 0,
      color: '#F59E0B',
      description: 'Maintenance and Repair',
    },
    {
      id: 'c1c4',
      title: 'C1-C4 - End of Life',
      icon: Recycle,
      activities: emissions.end_of_life_emissions,
      total: emissions.total_c1_c4 || 0,
      color: '#EF4444',
      description: 'Deconstruction, transport, waste processing, and disposal',
    },
  ];

  return (
    <div className="detailed-breakdown-container">
      <div className="breakdown-header">
        <h3 className="breakdown-title">Detailed Emissions</h3>
        <p className="breakdown-subtitle">Expand each stage to view activity-level emissions data</p>
      </div>

      <div className="breakdown-cards">
        {stages.map((stage, index) => {
          if (!stage.activities || Object.keys(stage.activities).length === 0) {
            return null;
          }

          const sortedActivities = Object.entries(stage.activities).sort((a, b) => b[1] - a[1]);
          const percentOfTotal =
            emissions.total_emissions > 0
              ? ((stage.total / emissions.total_emissions) * 100).toFixed(1)
              : '0.0';
          const isExpanded = expandedStages[index];
          const StageIcon = stage.icon;

          return (
            <div
              key={stage.id}
              id={`breakdown-card-${index}`}
              className={`breakdown-card ${isExpanded ? 'expanded' : ''}`}
            >
              <div
                className="breakdown-card-header"
                onClick={() => toggleStageExpansion(index)}
              >
                <div className="breakdown-card-header-left">
                  <div className="stage-color-bar" style={{ backgroundColor: stage.color }}></div>
                  <div
                    className="stage-icon-wrapper"
                    style={{ backgroundColor: `${stage.color}15` }}
                  >
                    <StageIcon size={20} style={{ color: stage.color }} />
                  </div>
                  <div className="stage-info">
                    <h3 className="stage-title">{stage.title}</h3>
                    <p className="stage-description">{stage.description}</p>
                  </div>
                </div>
                <div className="breakdown-card-header-right">
                  <div className="stage-metrics">
                    <div className="stage-total">
                      <span className="stage-total-value">
                        {formatEmissionValue(stage.total)}
                      </span>
                      <span className="stage-total-unit">kg CO₂e</span>
                    </div>
                    <div className="stage-percentage" style={{ color: stage.color }}>
                      {percentOfTotal}% of total
                    </div>
                  </div>
                  <div className="expand-icon">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="breakdown-card-body">
                  <div className="activities-table">
                    <div className="activities-table-header">
                      <div className="activities-table-header-cell">Activity</div>
                      <div className="activities-table-header-cell">Emissions</div>
                      <div className="activities-table-header-cell">Share of Stage</div>
                      <div className="activities-table-header-cell">Share of Total</div>
                    </div>
                    <div className="activities-table-body">
                      {sortedActivities.map(([activity, value]) => {
                        const stagePercent =
                          stage.total > 0 ? ((value / stage.total) * 100).toFixed(1) : '0.0';
                        const totalPercent =
                          emissions.total_emissions > 0
                            ? ((value / emissions.total_emissions) * 100).toFixed(1)
                            : '0.0';

                        return (
                          <div key={activity} className="activities-table-row">
                            <div className="activities-table-cell activity-name">
                              {formatActivityName(activity)}
                            </div>
                            <div className="activities-table-cell emissions-value">
                              <span className="emissions-number">
                                {formatEmissionValue(value)}
                              </span>
                              <span className="emissions-unit">kg CO₂e</span>
                            </div>
                            <div className="activities-table-cell">
                              <div className="progress-bar-container">
                                <div
                                  className="progress-bar"
                                  style={{ width: `${stagePercent}%`, backgroundColor: stage.color }}
                                ></div>
                                <span className="progress-label">{stagePercent}%</span>
                              </div>
                            </div>
                            <div className="activities-table-cell percentage-cell">
                              {totalPercent}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  
};
const ScenarioInfoPopover = React.forwardRef(({ scenario, onMouseEnter, onMouseLeave }, ref) => {
  if (!scenario) return null;

  const rawCreatedAt = scenario.created_at || scenario.date_created;

  // Format date without timezone abbreviation
  const formattedCreatedAt = (() => {
    if (!rawCreatedAt) return '—';
    
    // Add 'Z' if missing to force UTC interpretation
    let utcDateString = rawCreatedAt;
    if (typeof rawCreatedAt === 'string' && 
        !rawCreatedAt.endsWith('Z') && 
        !rawCreatedAt.includes('+')) {
      utcDateString = rawCreatedAt + 'Z';
    }
    
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return '—';
    
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
      // No timeZoneName - this removes EST, PST, etc.
    };
    
    if (scenario.timezone) {
      options.timeZone = scenario.timezone;
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  })();

  const userTimeZone = scenario.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div
      className="scenario-info-popover"
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="scenario-info-popover-header">
        <div className="scenario-info-popover-icon">
          <Info size={18} />
        </div>
        <div className="scenario-info-popover-title-group">
          <h3 className="scenario-info-popover-title">Scenario Details</h3>
          <p className="scenario-info-popover-subtitle">Key information about this scenario</p>
        </div>
      </div>

      <div className="scenario-info-popover-body">
        <dl className="scenario-info-grid">
          <div className="scenario-info-item">
            <dt>Scenario Name</dt>
            <dd>{scenario.name || '—'}</dd>
          </div>
          <div className="scenario-info-item">
            <dt>Plant</dt>
            <dd>{scenario.facility || '—'}</dd>
          </div>
          <div className="scenario-info-item">
            <dt>Created</dt>
            <dd>{formattedCreatedAt}</dd>
          </div>
          <div className="scenario-info-item">
            <dt>Time Zone</dt>
            <dd>{userTimeZone}</dd>
          </div>
        </dl>

        {scenario.comments && (
          <div className="scenario-info-comments">
            <h4>Comments</h4>
            <p>{scenario.comments}</p>
          </div>
        )}
      </div>
    </div>
  );
});

ScenarioInfoPopover.displayName = 'ScenarioInfoPopover';


export default Dashboard;
